import { useState } from "react";
import { parseCsv, normalizeCsvHeader } from "../shared/csv";
import { isValidEmail, normalizeEmail } from "../shared/encoding";
import type { AdminInvitee, Locale } from "../shared/types";
import { api } from "./api";
import { text } from "./ui";

export function matchSentEmails(rows: string[][], column: number, hasHeader: boolean, items: AdminInvitee[]) {
  const byEmail = new Map(items.map((item) => [normalizeEmail(item.email), item]));
  const seen = new Set<string>();
  return rows.slice(hasHeader ? 1 : 0).map((row, index) => {
    const email = normalizeEmail(row[column] || "");
    const item = byEmail.get(email);
    const state = !isValidEmail(email) ? "invalid" : seen.has(email) ? "duplicate" : !item ? "missing" : item.emailSentAt ? "already" : "match";
    seen.add(email);
    return { email, row: index + (hasHeader ? 2 : 1), state, id: item?.id };
  });
}

export function SentEmailImport({ eventId, locale, onDone }: { eventId: string; locale: Locale; onDone: () => void }) {
  const [rows, setRows] = useState<string[][]>([]);
  const [column, setColumn] = useState(0);
  const [hasHeader, setHasHeader] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const [preview, setPreview] = useState<Array<{ email: string; row: number; state: string; id?: string }> | null>(null);

  async function read(file: File) {
    setError(""); setResult(""); setPreview(null);
    try {
      if (file.size > 1_000_000) throw new Error(text(locale, "Dosya en fazla 1 MB olabilir.", "File must be no larger than 1 MB."));
      const parsed = parseCsv(await file.text());
      if (!parsed.length || parsed.length > 10001) throw new Error(text(locale, "En fazla 10.000 e-posta satırı yükleyin.", "Upload between 1 and 10,000 email rows."));
      const index = parsed[0].findIndex((value) => ["email", "emailaddress", "contactemail", "contactemailaddress", "eposta"].includes(normalizeCsvHeader(value)));
      setColumn(Math.max(0, index));
      setHasHeader(index >= 0 || !parsed[0].some(isValidEmail));
      setRows(parsed);
    } catch (e) { setError(e instanceof Error ? e.message : "Import failed."); }
  }

  async function review() {
    setBusy(true); setError("");
    try {
      const list = await api<{ items: AdminInvitee[] }>(`/api/admin/invitees?eventId=${encodeURIComponent(eventId)}&pageSize=all`);
      setPreview(matchSentEmails(rows, column, hasHeader, list.items));
    } catch (e) { setError(e instanceof Error ? e.message : "Preview failed."); }
    finally { setBusy(false); }
  }

  async function apply() {
    const ids = preview?.filter((row) => row.state === "match").map((row) => row.id!) || [];
    if (!ids.length) return;
    setBusy(true); setError("");
    try {
      const response = await api<{ updated: number }>(`/api/admin/invitees/email-status?eventId=${encodeURIComponent(eventId)}`, { method: "POST", body: JSON.stringify({ ids, sent: true }) });
      setResult(text(locale, `${response.updated} davetli gönderildi olarak işaretlendi. Diğer kayıtlar değişmedi.`, `${response.updated} invitees marked sent. Other records were unchanged.`));
      setRows([]); setPreview(null); onDone();
    } catch (e) { setError(e instanceof Error ? e.message : "Import failed."); }
    finally { setBusy(false); }
  }
  const labels: Record<string, string> = {
    match: text(locale, "Gönderildi işaretlenecek", "Will mark sent"), already: text(locale, "Zaten gönderildi", "Already marked sent"),
    invalid: text(locale, "Geçersiz / boş e-posta", "Invalid / empty email"), duplicate: text(locale, "CSV içinde tekrar", "Duplicate in CSV"), missing: text(locale, "Bu etkinlikte bulunamadı", "Not found in this event"),
  };
  return <div className="sent-email-import">
    <label className="outline-button csv-import-button">{text(locale, "Gönderilen e-postaları CSV ile işaretle", "Import sent emails CSV")}<input type="file" accept=".csv,.tsv,text/csv,text/tab-separated-values" disabled={busy} onChange={(e) => { const file = e.target.files?.[0]; if (file) void read(file); e.target.value = ""; }} /></label>
    {error && <p className="inline-error" role="alert">{error}</p>}
    {result && <p role="status">{result}</p>}
    {rows.length > 0 && <section className="sent-email-import__review">
      <h3>{text(locale, "Gönderilen e-postaları eşleştir", "Match sent emails")}</h3>
      <p>{text(locale, "Yalnızca gerçekten gönderilen e-postaların listesini yükleyin. Bu işlem e-posta göndermez; işaretleme zamanı şimdi olarak kaydedilir. Listede olmayan kişiler değiştirilmez.", "Upload only emails that were actually sent. This does not send email; the marking time is recorded as now. People absent from the file are unchanged.")}</p>
      <label>{text(locale, "E-posta sütunu", "Email column")}<select disabled={busy} value={column} onChange={(e) => { setColumn(Number(e.target.value)); setPreview(null); }}>{Array.from({ length: Math.max(...rows.map((row) => row.length)) }, (_, index) => <option key={index} value={index}>{index + 1}: {rows[0][index] || "—"}</option>)}</select></label>
      <label><input type="checkbox" disabled={busy} checked={hasHeader} onChange={(e) => { setHasHeader(e.target.checked); setPreview(null); }} /> {text(locale, "İlk satır sütun başlıklarıdır", "First row contains column headers")}</label>
      <div className="sent-email-import__actions"><button type="button" className="outline-button" disabled={busy} onClick={() => void review()}>{text(locale, "Eşleşmeleri önizle", "Preview matches")}</button><button type="button" className="outline-button" disabled={busy} onClick={() => { setRows([]); setPreview(null); setError(""); }}>{text(locale, "Vazgeç", "Cancel")}</button></div>
      {preview && <>
        <p>{Object.keys(labels).map((state) => `${labels[state]}: ${preview.filter((row) => row.state === state).length}`).join(" · ")}</p>
        <div className="sent-email-import__table"><table><thead><tr><th>#</th><th>Email</th><th>{text(locale, "Sonuç", "Result")}</th></tr></thead><tbody>{preview.map((row) => <tr key={row.row}><td>{row.row}</td><td>{row.email || "—"}</td><td>{labels[row.state]}</td></tr>)}</tbody></table></div>
        <button type="button" className="primary-button" disabled={busy || !preview.some((row) => row.state === "match")} onClick={() => void apply()}>{busy ? text(locale, "İşleniyor…", "Working…") : text(locale, "Onayla ve eşleşenleri gönderildi işaretle", "Confirm and mark matches sent")}</button>
      </>}
    </section>}
  </div>;
}
